import { createContext, type ReactNode, useContext, useMemo, useState } from 'react';
import { useInvoiceParamsPresence, usePhieuNhapKhoExport } from '../hooks';
import { MA_CHUNG_TU, QUERY_KEYS } from '@/constants';
import { useCRUD } from '@/hooks';

interface PhieuNhapKhoSearchContextValue {
  exportToExcel: () => Promise<void>;
  canExport: boolean;
  isExporting: boolean;
  hasInvoiceParams: boolean;
  isImportDialogOpen: boolean;
  openImportDialog: () => void;
  closeImportDialog: () => void;
  transactionOptions: { value: string; label: string }[];
}

const PhieuNhapKhoSearchContext = createContext<PhieuNhapKhoSearchContextValue | null>(null);

export function usePhieuNhapKhoSearchContext() {
  const context = useContext(PhieuNhapKhoSearchContext);

  if (!context) {
    throw new Error('usePhieuNhapKhoSearchContext must be used within PhieuNhapKhoSearchProvider');
  }

  return context;
}

export function PhieuNhapKhoSearchProvider({ children }: { children: ReactNode }) {
  const { exportToExcel, canExport, isExporting } = usePhieuNhapKhoExport();
  const hasInvoiceParams = useInvoiceParamsPresence();
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const { data } = useCRUD({
    endpoint: `${QUERY_KEYS.MA_GIAO_DICH}?ma_ct=${MA_CHUNG_TU.KHO.PHIEU_NHAP_KHO}`
  });

  const transactionOptions = useMemo(
    () =>
      data.map(item => ({
        value: item.uuid,
        label: item.ten_gd
      })),
    [data]
  );

  const value = useMemo(
    () => ({
      exportToExcel,
      canExport,
      isExporting,
      hasInvoiceParams,
      isImportDialogOpen,
      openImportDialog: () => setIsImportDialogOpen(true),
      closeImportDialog: () => setIsImportDialogOpen(false),
      transactionOptions
    }),
    [canExport, exportToExcel, hasInvoiceParams, isExporting, isImportDialogOpen, transactionOptions]
  );

  return <PhieuNhapKhoSearchContext.Provider value={value}>{children}</PhieuNhapKhoSearchContext.Provider>;
}
